import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import { type ComboBoxProps } from '@discover-ui/comboBox';
import useDate from '@discover-data/stores/hooks/date/useDate';
import { todayDateInTimeZone } from '@discover-data/stores/hooks/date/today';
import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import { useRegionRollover } from '@/components/hooks/useRegionRollover';

const ComboBox = dynamic(() => import('@discover-ui/comboBox'));

export type DateSelectProps = Omit<
  ComboBoxProps,
  'options' | 'value' | 'setValue' | 'placeholder' | 'description'
> & {
  datesToShow?: ReadonlyArray<string>;
};

export default function DateSelect({ triggerProps, datesToShow, ...props }: DateSelectProps) {
  const storeDiscountDatesToShow = useStoreState((state) => state.discountDatesToShow);
  const discountDatesToShow = datesToShow ?? storeDiscountDatesToShow;
  const [date, setDate] = useDate();
  const { track } = useAnalytics();
  const { timeZone } = useRegionRollover();
  const todayDate = todayDateInTimeZone(timeZone);

  const handleSelectDate = (newDate: string | undefined) => {
    const selectedDate = newDate ?? 'week';
    track('date_selected', { option_selected_id: selectedDate });
    return setDate(selectedDate);
  };

  return (
    <TrackProvider button_name="date picker">
      <ComboBox
        options={[
          {
            value: 'week',
            label: 'All dates',
          },
          ...discountDatesToShow.map((dateToShow) => {
            const dateLabel = dayjs(dateToShow).format('ddd DD MMM');
            return {
              value: dateToShow,
              label: dateToShow === todayDate ? 'Today' : dateLabel,
            };
          }),
        ]}
        value={date}
        setValue={handleSelectDate}
        placeholder="Pick a date"
        description="Select a date from the list"
        triggerProps={triggerProps}
        {...props}
      />
    </TrackProvider>
  );
}
